import { useEffect, useMemo, useState } from 'react';
import { Search, Check, Loader2, AlertCircle, RefreshCcw } from 'lucide-react';
import { buildApiUrl } from '../lib/apiBase';

export interface GuestRepository {
  id: number;
  name: string;
  fullName: string;
  owner: string;
  isPrivate: boolean;
}

interface GuestRepoPickerProps {
  selectedRepoId: number | null;
  onSelect: (repo: GuestRepository) => void;
  className?: string;
}

export function GuestRepoPicker({ selectedRepoId, onSelect, className = '' }: GuestRepoPickerProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [repositories, setRepositories] = useState<GuestRepository[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadRepositories = async (forceRefresh = false) => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await fetch(`${buildApiUrl('/api/v1/guest/github/repositories')}?forceRefresh=${forceRefresh}`, {
        credentials: 'include',
      });
      if (!response.ok) {
        throw new Error('Failed to load repositories');
      }
      const data = await response.json();
      setRepositories(data.repositories || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load repositories');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    void loadRepositories(false);
  }, []);

  const filteredRepos = useMemo(() => {
    return repositories.filter(repo =>
      repo.fullName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      repo.name.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [repositories, searchQuery]);

  if (isLoading) {
    return (
      <div className={`border border-slate-700 rounded-lg p-6 bg-slate-800 ${className}`}>
        <div className="flex items-center justify-center gap-2 text-slate-400">
          <Loader2 className="w-5 h-5 animate-spin" />
          <span>Loading repositories...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`border border-red-900/50 rounded-lg p-6 bg-red-950/20 ${className}`}>
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-red-400 font-medium mb-1">Failed to load repositories</p>
            <p className="text-red-300/70 text-sm">{error}</p>
          </div>
        </div>
        <button
          onClick={() => void loadRepositories(true)}
          className="mt-4 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-md transition-colors"
        >
          Retry
        </button>
      </div>
    );
  }

  if (repositories.length === 0) {
    return (
      <div className={`border border-slate-700 rounded-lg p-6 bg-slate-800 ${className}`}>
        <p className="text-slate-400 text-center">
          No repositories available. Make sure the GitHub App has access to at least one repository.
        </p>
        <button
          onClick={() => void loadRepositories(true)}
          className="mt-4 w-full px-4 py-2 bg-slate-700 hover:bg-slate-600 text-slate-100 rounded-md transition-colors flex items-center justify-center gap-2"
        >
          <RefreshCcw className="w-4 h-4" />
          Refresh
        </button>
      </div>
    );
  }

  return (
    <div className={`border border-slate-700 rounded-lg bg-slate-800 ${className}`}>
      <div className="p-4 border-b border-slate-700">
        <div className="flex items-center justify-between mb-2">
          <label className="block text-sm font-medium text-slate-300">
            Repository (select one)
          </label>
          <button
            onClick={() => void loadRepositories(true)}
            className="text-xs text-slate-400 hover:text-slate-200 flex items-center gap-1"
          >
            <RefreshCcw className="w-3 h-3" />
            Refresh
          </button>
        </div>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
          <input
            type="text"
            placeholder="Search repositories..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 bg-slate-900 border border-slate-700 rounded-md text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
      </div>

      <div className="p-4">
        <div className="space-y-1 max-h-96 overflow-y-auto">
          {filteredRepos.length === 0 ? (
            <p className="text-slate-500 text-center py-8">
              {searchQuery ? 'No repositories match your search' : 'No repositories available'}
            </p>
          ) : (
            filteredRepos.map((repo) => {
              const isSelected = selectedRepoId === repo.id;
              return (
                <button
                  key={repo.id}
                  onClick={() => onSelect(repo)}
                  className={`
                    w-full px-3 py-2 rounded-md text-left transition-colors flex items-center justify-between
                    ${isSelected
                      ? 'bg-blue-600/20 border border-blue-500/50'
                      : 'hover:bg-slate-700/50 border border-transparent'
                    }
                  `}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-white font-medium truncate">{repo.name}</span>
                      {repo.isPrivate && (
                        <span className="text-xs px-1.5 py-0.5 bg-slate-700 text-slate-300 rounded">
                          Private
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-slate-500 truncate">{repo.fullName}</p>
                  </div>
                  {isSelected && (
                    <Check className="w-5 h-5 text-blue-500 flex-shrink-0 ml-2" />
                  )}
                </button>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}
